import { useCallback, useRef } from 'react';

// The Combobox fires onInputValueChange for many reasons — selecting an item
// emits the item's label ('item-press') and blur can revert the text. Only
// these mean the user actually edited the query, so only they drive a search.
export const SEARCH_EDIT_REASONS: string[] = [
  'input-change',
  'input-clear',
  'input-paste',
];

interface LoadMorePaging {
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => void;
}

// Load-more: the Combobox mounts its popup lazily, so the sentinel only
// exists once the list is open. The returned callback ref attaches the
// observer exactly when the sentinel mounts; a ref holds the latest paging
// values so the observer never needs re-creating.
export const useLoadMoreSentinel = (paging: LoadMorePaging) => {
  const pagingRef = useRef(paging);
  pagingRef.current = paging;
  const observerRef = useRef<IntersectionObserver | null>(null);

  return useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) {
      observerRef.current = null;
      return;
    }
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const currentPaging = pagingRef.current;
        if (
          entries[0].isIntersecting &&
          currentPaging.hasNextPage &&
          !currentPaging.isFetchingNextPage
        ) {
          currentPaging.fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observerRef.current.observe(node);
  }, []);
};
